import {BlockDefinitionResponse} from '@mintdata-wsp/common/controllers/network/extended/responses/BlockDefinitionResponse';
import {Bundle} from '@mintdata-wsp/common/controllers/network/extended/responses/Bundle';
import {CategoryResponse} from '@mintdata-wsp/common/controllers/network/extended/responses/CategoryResponse';
import {PropertyResponse} from '@mintdata-wsp/common/controllers/network/extended/responses/PropertyResponse';
import {ArtifactPojo} from '@mintdata-wsp/common/controllers/network/generated/requests/ArtifactPojo';
import {ResolutionRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ResolutionRequest';
import {CloudArtifactResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CloudArtifactResponse';
import {CloudArtifactsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CloudArtifactsResponse';
import {PortDefinitionResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PortDefinitionResponse';
import {ResolutionResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/ResolutionResponse';
import {TickDefinitionResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/TickDefinitionResponse';
import {ICloudComponentsLoader} from '@mintdata-wsp/common/controllers/artifacts/CloudComponentsLoader';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {
    CloudCategory,
    CloudComponent,
    CloudComponentGroup,
    CloudPort,
    CloudProperty,
    CloudTick,
    ResolvedCloudArtifact,
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {action} from 'mobx';

export class CloudComponentsLoader implements ICloudComponentsLoader
{
    private static toMavenDescriptor(cr:CloudArtifactResponse):string
    {
        return `${cr.groupId}:${cr.artifactId}:${cr.version}`;
    }

    @action.bound
    public loadAndResolveAllCloudArtifacts():Promise<ResolvedCloudArtifact[]>
    {
        return API.resolution
            .searchComponentSets('*')
            .then((resp:CloudArtifactsResponse) =>
            {
                const respArtifacts = resp.artifacts;
                const artifactPojos = respArtifacts.map((cr:CloudArtifactResponse) =>
                {
                    const mvnDescriptor = CloudComponentsLoader.toMavenDescriptor(cr);
                    return new ArtifactPojo('MAVEN', mvnDescriptor);
                });

                return {respArtifacts, artifactPojos};
            })
            .then(({respArtifacts, artifactPojos}) =>
            {
                return API.resolution
                    .resolve(new ResolutionRequest(artifactPojos))
                    .then(action((resp:ResolutionResponse) =>
                    {
                        const allCloudArtifacts:ResolvedCloudArtifact[] =
                            resp.bundles.map((bundle) =>
                            {
                                const artifact = bundle.artifact;
                                const cr = respArtifacts.find((cr:CloudArtifactResponse) =>
                                {
                                    const bundleDescriptor = bundle.artifact.descriptor;
                                    const csDescriptor = CloudComponentsLoader.toMavenDescriptor(cr);

                                    return bundleDescriptor === csDescriptor;
                                });

                                // TODO: determine why API.resolution.reolve() returns more Bundles
                                // than artifactPojo(s) passed to it.
                                if( !cr )
                                    return null;

                                const resolvedCloudArtifact = this._bundleToResolvedCloudArtifact(
                                    cr.groupId,
                                    cr.artifactId,
                                    cr.version,
                                    bundle);

                                return resolvedCloudArtifact;
                            });

                        const resolvedCloudArtifacts:ResolvedCloudArtifact[] = allCloudArtifacts.filter((rca) => (rca != null));
                        return resolvedCloudArtifacts;
                    }));
            });
    }

    private _bundleToResolvedCloudArtifact(artifactGroupId:string,
                                           artifactId:string,
                                           artifactVersion:string,
                                           bundle:Bundle):ResolvedCloudArtifact
    {
        const artifactName = bundle.artifact.name;
        const cloudComponents = bundle.components.map((bdr:BlockDefinitionResponse) =>
        {
            const componentName = bdr.name;
            const sectionName = bdr.section;
            const groupId = `${artifactGroupId}:${artifactId}:${artifactVersion}`;

            const group = new CloudComponentGroup(groupId, artifactName, artifactName, sectionName);

            const inputPorts = bdr.inputs.map((p:PortDefinitionResponse) =>
            {
                const name = p.name;
                const description = p.description;
                const isOptional = p.optional;

                return new CloudPort(name, description, isOptional);
            });

            const outputPorts = bdr.outputs.map((p:PortDefinitionResponse) =>
            {
                const name = p.name;
                const description = p.description;
                const isOptional = p.optional;

                return new CloudPort(name, description, false);
            });

            const properties:CloudProperty[] = bdr.properties.map((p:PropertyResponse) => (
                CloudProperty.fromResponse(p)
            ));

            const categories:CloudCategory[] = bdr.categories.map((c:CategoryResponse) => (
                CloudCategory.fromResponse(c)
            ));

            const ticks:CloudTick[] = bdr.ticks.map((t:TickDefinitionResponse) =>
            {
                return new CloudTick(t.name, t.description, t.interval, t.unit);
            });

            return new CloudComponent(
                group,
                bdr.clazz,
                componentName,
                bdr.description,
                properties,
                categories,
                ticks,
                inputPorts,
                outputPorts,
            );
        });

        return new ResolvedCloudArtifact(artifactName, artifactGroupId, artifactId, artifactVersion, cloudComponents);
    }
}
